import React from "react";

/* Shared pieces of the Interpretation Review page. Touch targets are at least 40px (tablet use). */

export const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`bg-white rounded-xl border border-[#E5E7EB] ${className}`}>{children}</div>
);

export const SectionTitle = ({ children, right = null }) => (
  <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
    <h3 className="text-[13px] font-semibold uppercase tracking-[0.6px] text-[#6B7280]">{children}</h3>
    {right}
  </div>
);

// The worksheet input summary: [{label, value} | {section}] rows from the server, same for every study type.
export const WorksheetTable = ({ rows }) => {
  if (!rows || !rows.length) return <p className="px-4 pb-4 text-[13px] text-[#6B7280]">No worksheet values.</p>;
  return (
    <table className="w-full text-[13px] border-collapse" data-testid="worksheet">
      <tbody>
        {rows.map((r, i) =>
          r.section ? (
            <tr key={`s${i}`}>
              <td colSpan={2} className="px-4 pt-3 pb-1 text-[12px] font-semibold text-[#1D2951]">{r.section}</td>
            </tr>
          ) : (
            <tr key={`r${i}`} className="border-t border-[#F1F2F4] align-top">
              <td className="px-4 py-1.5 text-[#6B7280] w-[42%] capitalize">{r.label}</td>
              <td className="px-4 py-1.5 text-[#18181B] whitespace-pre-wrap break-words">{r.value}</td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
};

// One report, rendered the same way for A and B (and for an echo draft): plain text, monospace, wrapped.
export const ReportText = ({ text, testid }) => (
  <pre data-testid={testid}
    className="m-0 whitespace-pre-wrap break-words font-mono text-[13px] leading-[1.5] text-[#18181B] bg-[#FAFAF9] border border-[#E5E7EB] rounded-lg p-3 max-h-[60vh] overflow-auto">
    {text}
  </pre>
);

// A row of choice buttons (1-5 scores, A / B / Tie, Yes / No).
export const Segmented = ({ options, value, onChange, label, disabled = false, testid }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label} data-testid={testid}>
    {options.map(([v, text]) => {
      const on = value === v;
      return (
        <button key={String(v)} type="button" role="radio" aria-checked={on} disabled={disabled}
          onClick={() => onChange(on ? null : v)}
          className={`min-w-[40px] h-10 px-3 rounded-lg border text-[14px] font-medium transition-colors disabled:opacity-60 ${
            on ? "border-[#1D2951] bg-[#1D2951] text-white" : "border-[#D7DCE3] bg-white text-[#1D2951] hover:bg-[#F2F6FC]"}`}>
          {text}
        </button>
      );
    })}
  </div>
);

export const ProgressBar = ({ done, total, label }) => {
  const pct = total ? Math.round((100 * done) / total) : 0;
  return (
    <div className="flex items-center gap-3" aria-label={label}>
      <div className="w-40 h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
        <div className="h-full bg-[#2F7D4F]" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[13px] font-semibold text-[#1D2951]" data-testid="progress-count">{done}/{total}</span>
    </div>
  );
};

export const StatusPill = ({ status, decision }) => {
  const map = {
    new: ["Not started", "bg-[#F3F4F6] text-[#4B5563]"],
    draft: ["Draft", "bg-amber-50 text-amber-800"],
    submitted: ["Submitted", "bg-emerald-50 text-emerald-800"],
    approve: ["Approved", "bg-emerald-50 text-emerald-800"],
    approve_with_edits: ["Approved with edits", "bg-sky-50 text-sky-800"],
    reject: ["Rejected", "bg-red-50 text-red-800"],
  };
  const key = status === "submitted" && decision ? decision : status;
  const [text, cls] = map[key] || map.new;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{text}</span>;
};

export const Button = ({ children, variant = "primary", className = "", ...rest }) => {
  const styles = {
    primary: "bg-[#1D2951] text-white hover:bg-[#2A3A6B] border-[#1D2951]",
    secondary: "bg-white text-[#1D2951] hover:bg-[#F2F6FC] border-[#D7DCE3]",
  };
  return (
    <button type="button" {...rest}
      className={`h-10 px-4 rounded-lg border text-[14px] font-medium disabled:opacity-50 ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const DraftBanner = () => (
  <div role="note" data-testid="draft-banner"
    className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-900">
    Draft — not filed to chart. This is a review/study tool: nothing here is signed or sent to Athena.
  </div>
);
